import { gql } from '@apollo/client';

const API_BASE = process.env.REACT_APP_GRAPHQL_ENDPOINT?.replace('/graphql', '') ?? '';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

export interface Cidade {
  id: string;
  nome: string;
  uf: string;
  quantidadeQuadras: number;
}

export interface SlotDisponivel {
  horaInicio: string;
  horaFim: string;
  disponivel: boolean;
  motivo?: string | null;
}

export interface QuadraDisponivel {
  id: string;
  nome: string;
  descricao: string;
  tipoPiso: string;
  imagens: { id: string; url: string; ordem: number }[];
  local: {
    id: string;
    nome: string;
    endereco: {
      logradouro: string;
      numero: string;
      bairro: string;
      cidade: { nome: string; uf: string };
    };
  };
  slots: SlotDisponivel[];
}

export const listarCidades = async (): Promise<Cidade[]> => {
  const res = await fetch(`${API_BASE}/api/quadras/cidades`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Erro ao buscar cidades');
  return res.json();
};

export const buscarQuadrasDisponiveis = async (
  cidadeId?: string,
  data?: string,
  tipoPiso?: string,
  localId?: string,
): Promise<QuadraDisponivel[]> => {
  const params = new URLSearchParams();
  if (cidadeId) params.append('cidadeId', cidadeId);
  if (localId) params.append('localId', localId);
  if (data) params.append('data', data);
  if (tipoPiso) params.append('tipoPiso', tipoPiso);
  const res = await fetch(`${API_BASE}/api/quadras/disponibilidade?${params}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Erro ao buscar quadras');
  return res.json();
};

export const LISTAR_CIDADES = gql`
  query ListarCidades {
    cidadesComQuadras {
      id
      nome
      uf
      quantidadeQuadras
    }
  }
`;

export const LOCAIS_POR_CIDADE = gql`
  query ListarLocais($cidadeId: ID, $aluguelHabilitado: Boolean) {
    locais(cidadeId: $cidadeId, aluguelHabilitado: $aluguelHabilitado) {
      id
      nome
      descricao
      aluguelHabilitado
      urlLocalizacao
      endereco {
        logradouro
        numero
        bairro
        cidade { id nome uf }
      }
    }
  }
`;

export const DISPONIBILIDADE_QUADRA = gql`
  query DisponibilidadeQuadra($quadraId: ID!, $data: String!) {
    disponibilidadeQuadra(quadraId: $quadraId, data: $data) {
      horaInicio
      horaFim
      disponivel
      motivo
    }
  }
`;

export const DISPONIBILIDADE_QUADRAS_BATCH = gql`
  query DisponibilidadeQuadrasBatch($quadraIds: [ID!]!, $data: String!) {
    disponibilidadeQuadrasBatch(quadraIds: $quadraIds, data: $data) {
      quadraId
      slots {
        horaInicio
        horaFim
        disponivel
        motivo
      }
    }
  }
`;

// Disponibilidade agregada por local numa cidade/data — usada para ordenar a
// listagem por "maior disponibilidade". Uma única query, calculada em lote no back.
export const DISPONIBILIDADE_POR_LOCAL_NA_CIDADE = gql`
  query DisponibilidadePorLocalNaCidade($cidadeId: ID!, $data: String!) {
    disponibilidadePorLocalNaCidade(cidadeId: $cidadeId, data: $data) {
      localId
      slotsLivres
    }
  }
`;

export const QUADRA_POR_ID = gql`
  query QuadraPorId($id: ID!) {
    quadraPorId(id: $id) {
      id
      nome
      descricao
      tipoPiso
      observacao
      imagens { id url ordem }
      ativa
      duracaoSlotMinutos
      local {
        id
        nome
        descricao
        urlLocalizacao
        urlLocalizacao
        endereco {
          logradouro
          numero
          complemento
          bairro
          cep
          cidade { nome uf }
        }
      }
    }
  }
`;

export const PRECOS_QUADRA = gql`
  query PrecosQuadra($quadraId: ID!) {
    precosQuadra(quadraId: $quadraId) {
      id
      diaSemana
      horaInicio
      horaFim
      valorPorHora
    }
  }
`;

export const CALCULAR_VALOR = gql`
  query CalcularValorAgendamento($quadraId: ID!, $dataAgendamento: String!, $horaInicio: String!, $horaFim: String!) {
    calcularValorAgendamento(
      quadraId: $quadraId
      dataAgendamento: $dataAgendamento
      horaInicio: $horaInicio
      horaFim: $horaFim
    )
  }
`;

export const CRIAR_AGENDAMENTO = gql`
  mutation CriarAgendamento($input: AgendamentoInput!) {
    criarAgendamento(input: $input) {
      id
      status
      valor
      reservaExpiraEm
    }
  }
`;

export const CRIAR_PEDIDO = gql`
  mutation CriarPedido($input: PedidoAgendamentoInput!) {
    criarPedidoAgendamento(input: $input) {
      id
      status
      valorTotal
      reservaExpiraEm
      local { id nome }
      agendamentos {
        id
        quadra { id nome }
        dataAgendamento
        horaInicio
        horaFim
        valor
        status
      }
    }
  }
`;

export const CANCELAR_ITEM_PEDIDO = gql`
  mutation CancelarItemPedido($pedidoId: ID!, $agendamentoId: ID!) {
    cancelarItemPedido(pedidoId: $pedidoId, agendamentoId: $agendamentoId) {
      id
      status
      valorTotal
      agendamentos { id status valor horaInicio horaFim }
    }
  }
`;

export const CANCELAR_PEDIDO = gql`
  mutation CancelarPedido($id: ID!) {
    cancelarPedidoAgendamento(id: $id) {
      id
      status
    }
  }
`;

export const PEDIDO_POR_ID = gql`
  query PedidoPorId($id: ID!) {
    pedidoAgendamentoPorId(id: $id) {
      id
      status
      valorTotal
      reservaExpiraEm
      local {
        id
        nome
        janelaHorasReembolso
        urlLocalizacao
        endereco {
          logradouro
          numero
          complemento
          bairro
          cep
          cidade { nome uf }
        }
      }
      agendamentos {
        id
        quadra { id nome duracaoSlotMinutos }
        dataAgendamento
        horaInicio
        horaFim
        valor
        valorOriginal
        percentualDescontoAluno
        status
        tipoAtor
      }
    }
  }
`;

export const MEUS_PEDIDOS = gql`
  query MeusPedidos {
    meusPedidosAgendamento {
      id
      status
      valorTotal
      reservaExpiraEm
      local {
        id
        nome
        urlLocalizacao
        endereco {
          logradouro
          numero
          bairro
          cidade { nome uf }
        }
      }
      agendamentos {
        id
        quadra { id nome duracaoSlotMinutos }
        dataAgendamento
        horaInicio
        horaFim
        valor
        status
        tipoAtor
      }
      criadoEm
    }
  }
`;

export const GERAR_LINK_PAGAMENTO = gql`
  mutation GerarLinkPagamentoAgendamento($id: ID!) {
    gerarLinkPagamentoAgendamento(id: $id) {
      id
      invoiceId
      linkPagamento
      linkExpiraEm
    }
  }
`;

export const CANCELAR_AGENDAMENTO = gql`
  mutation CancelarAgendamento($id: ID!, $motivo: String) {
    cancelarAgendamento(id: $id, motivo: $motivo) {
      agendamento { id status motivoCancelamento }
      multaAplicada
    }
  }
`;

export const MEUS_AGENDAMENTOS = gql`
  query MeusAgendamentos {
    agendamentosDoAtleta {
      id
      dataAgendamento
      horaInicio
      horaFim
      status
      valor
      invoiceId
      observacao
      tipoAtor
      reservaExpiraEm
      criadoEm
      quadra {
        id
        nome
        imagens { id url ordem }
        local { nome endereco { cidade { nome uf } } }
      }
    }
  }
`;

export const TIPO_PISO_LABEL: Record<string, string> = {
  SAIBRO: 'Saibro',
  PISO_DURO: 'Piso Duro',
  GRAMA: 'Grama',
  SINTETICO: 'Sintético',
  AREIA: 'Areia',
};

export const DIA_SEMANA_LABEL: Record<string, string> = {
  SEGUNDA: 'Segunda',
  TERCA: 'Terça',
  QUARTA: 'Quarta',
  QUINTA: 'Quinta',
  SEXTA: 'Sexta',
  SABADO: 'Sábado',
  DOMINGO: 'Domingo',
};

export const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  RESERVADO: { label: 'Reservado', color: 'warning' },
  A_PAGAR: { label: 'Aguardando Pagamento', color: 'warning' },
  CONFIRMADO: { label: 'Confirmado', color: 'success' },
  CANCELADO: { label: 'Cancelado', color: 'danger' },
  EXPIRADO: { label: 'Expirado', color: 'danger' },
  CONCLUIDO: { label: 'Concluído', color: 'info' },
};

export const STATUS_PEDIDO_LABEL: Record<string, { label: string; color: string }> = {
  ABERTO: { label: 'Pendente', color: 'warning' },
  AGUARDANDO_PAGAMENTO: { label: 'Aguardando Pagamento', color: 'warning' },
  PAGO: { label: 'Pago', color: 'success' },
  CANCELADO: { label: 'Cancelado', color: 'danger' },
  EXPIRADO: { label: 'Expirado', color: 'danger' },
};

export const TIPOS_PISO = ['SAIBRO', 'PISO_DURO', 'GRAMA', 'SINTETICO', 'AREIA'] as const;

export const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const todayISO = () => new Date().toISOString().split('T')[0];
